import Logo from "@/assets/icons/logo.svg?react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CssBaseline,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FC, FormEvent, useState } from "react";
import { z } from "zod";
import { useSetAdminPassword } from "../../queries";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const initialFormState = {
  password: "",
  repeatedPassword: "",
};

const formSchema = z
  .object({
    password: z
      .string()
      .min(1, "Field is required")
      .max(20, "Field must be at most 20 characters"),
    repeatedPassword: z.string().min(1, "Field is required"),
  })
  .refine((data) => data.password === data.repeatedPassword, {
    message: "Passwords do not match",
    path: ["repeatedPassword"],
  });

type FormData = z.infer<typeof formSchema>;

const SetPasswordPage: FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const { setAdminPassword, isSetAdminPasswordLoading } = useSetAdminPassword();

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState(false);
  const [touched, setTouched] = useState({
    password: false,
    repeatedPassword: false,
  });
  const [adminUserFormData, setAdminUserFormData] = useState<Partial<FormData>>(
    {}
  );

  const formData: FormData = {
    ...initialFormState,
    ...adminUserFormData,
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (token) {
      setAdminPassword({
        token,
        newPassword: formData.password,
      });
    } else {
      toast.error("Token from email was not provided!");
    }
  };

  const validate = () => {
    const res = formSchema.safeParse(formData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const errors = validate();
  const isFormFilled = Object.values(formData).every((v) => !!v);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        p: 2,
        pt: 20,
        alignItems: "center",
        margin: "0 auto",
        maxWidth: "500px",
      }}
    >
      <CssBaseline />
      <Logo width={"100%"} />

      <Box component="form" width="100%" onSubmit={onSubmit}>
        <Stack justifyContent={"flex-start"} width={"100%"}>
          <Typography variant="h4" mt={4}>
            Set Up Your Password
          </Typography>
          <Typography variant="body2" sx={{ mb: 4 }}>
            For your security, please set up your password to access your
            account.
          </Typography>
        </Stack>

        <Stack spacing={2} mb={3}>
          <TextField
            fullWidth
            variant="filled"
            autoComplete="new-password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            label={
              touched.password && errors?.password
                ? errors.password._errors[0]
                : "Password"
            }
            error={touched.password && !!errors?.password}
            value={formData.password}
            onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
            onChange={(e) =>
              setAdminUserFormData((prev) => ({
                ...prev,
                password: e.target.value,
              }))
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            variant="filled"
            autoComplete="new-password"
            type={showRepeatedPassword ? "text" : "password"}
            placeholder="Repeat Password"
            label={
              touched.repeatedPassword && errors?.repeatedPassword
                ? errors.repeatedPassword._errors[0]
                : "Repeat Password"
            }
            error={touched.repeatedPassword && !!errors?.repeatedPassword}
            value={formData.repeatedPassword}
            onBlur={() =>
              setTouched((prev) => ({ ...prev, repeatedPassword: true }))
            }
            onChange={(e) =>
              setAdminUserFormData((prev) => ({
                ...prev,
                repeatedPassword: e.target.value,
              }))
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowRepeatedPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <Button
          fullWidth
          color="secondary"
          variant="contained"
          type="submit"
          loading={isSetAdminPasswordLoading}
          disabled={!isFormFilled || !!errors || isSetAdminPasswordLoading}
        >
          Set Password
        </Button>
      </Box>

      <Box
        sx={{
          position: "absolute",
          left: 0,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          bottom: 10,
        }}
      >
        <Typography variant="body2" align="center">
          © 2024 Société.&nbsp;|&nbsp;
          <Link
            href="https://sunset.app/privacy-policy"
            color="inherit"
            underline="hover"
            target="_blank"
          >
            Privacy Policy
          </Link>{" "}
          &nbsp;|&nbsp;
          <Link
            href="https://sunset.app/terms-and-conditions"
            color="inherit"
            underline="hover"
            target="_blank"
          >
            Terms Of Use
          </Link>{" "}
          &nbsp;|&nbsp;
          <Link
            href="mailto:info@sunset.app"
            underline="hover"
            target="_blank"
          >
            info@sunset.app
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export { SetPasswordPage };
